import { useEffect, useState } from "react";
import _ from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftRotate } from "@fortawesome/free-solid-svg-icons";
import Button from "src/components/base/Button/Button";
import CAFilterContainer, {
    AvailabilityFilter,
} from "src/components/modules/ClinitianAvailabilityFilter/CAFilterContainer";
import { deepClone } from "src/utils/helpers";
import { Typography } from "src/latest/src/components";

type Props = {
    selectedFilters: Record<string, any>; // Replace `any` with a more specific type if possible
    setSelectedFilters?: (filters: Record<string, any>) => void; // Define the function shape
    searchUpdatedFilter: Record<string, any>; // Replace `Object` with a specific type
    setSearchUpdatedFilter: (filters: Record<string, any>) => void; // Define the function shape
    handleAvailabilityFilter: (filter: AvailabilityFilter) => void; // Define the function shape
    calenderServiceFilters: Array<Record<string, any>>; // Replace `any` with a more specific type if possible
    handleScheduleFilterUpdate: (filter: any, selectedValue: any[], currentFilter: any) => void; // Define the function shape
    setAgeGroupFilter?: (ageGroup: string) => void; // Define the function shape
    insuranceList?: Array<Record<string, any>>; // Replace `any` with a more specific type if possible
    applyFilter?: (filters: Record<string, any>) => void; // Define the function shape
    availabilityFilter: AvailabilityFilter;
    handleSelectedFilterUpdate?: (filters: Record<string, any>) => void; // Define the function shape
};

export default function ApplyFilterModal({
    selectedFilters,
    searchUpdatedFilter,
    setSearchUpdatedFilter,
    handleAvailabilityFilter,
    calenderServiceFilters,
    handleScheduleFilterUpdate,
    insuranceList,
    applyFilter,
    availabilityFilter,
    handleSelectedFilterUpdate,
}: Props) {
    const searchFilters = [
        "insurancePlan",
        "officeLocation",
        "ageGroup",
        "speciality",
        "language",
        "fees",
    ]; // filters which get updated via setSearchUpdatedFilter function

    const [_searchUpdatedFilter, _setSearchUpdatedFilters] =
        useState(searchUpdatedFilter);

    const [_selectedFilters, _setSelectedFilters] = useState<any>(); // filters that are applied by user
    let _updatedSelectedFilters: any;

    useEffect(() => {
        _updatedSelectedFilters = _.cloneDeep(selectedFilters);

        _setSelectedFilters(_updatedSelectedFilters);
    }, []);

    function _handleSelectedFilters(params: any): void {
        let unselectedFilters: string[] = deepClone(searchFilters);

        _setSearchUpdatedFilters(params);
        setSearchUpdatedFilter(params);

        // we need to filter the selction based on filter_selection_data
        // because for fees filter if we delete all the values,
        // the filter component returning the filter in params but with empty filter_selection_data array
        // for other filters if nothing is selcted, it just do not return the filter at all
        // so we need to add this exclusive condition to treat filter_selection_data = [] as nothing is selected for that filter
        const paramsWithDataSelection = params.filter(
            (filter: any) => filter.filter_selection_data.length
        );

        paramsWithDataSelection?.forEach((filter: any) => {
            // as filter container only return the values of selected filters/
            // we need to mark all other filters as unselected.
            // for that we need to differntiate between selected filter vs non selected filters
            // that is why we need to maintain the array of all filters available
            // and here we filterd out between selected vs non-selected ones
            unselectedFilters = unselectedFilters.filter(
                (fltr) => fltr !== filter.filter_key
            );

            let selectedValues: string[];

            if (filter?.filter_component_type === "text-field" ||
                filter?.filter_component_type === "location-search"
            ) {
                selectedValues = filter?.filter_selection_data?.map(
                    (fltr: any) => fltr.value
                );
            } else {
                selectedValues = filter?.filter_selection_data?.map(
                    (fltr: any) => fltr.unique_key
                );
            }

            _updateLocalFilterState(
                filter?.filter_key,
                selectedValues,
                filter?.filter_component_type
            );
        });

        // as we need to set all non selected ones as unselected
        // we need to update ther status here to make then not selected
        unselectedFilters?.forEach((filterKey: string) => {
            _updateLocalFilterState(filterKey);
        });
    }

    function _handleAvailabilityFilter(params: AvailabilityFilter): void {
        handleAvailabilityFilter(params);
    }

    function _handleScheduleFilterUpdate(
        filter: any,
        selectedValue: any[],
        currentFilter: any
    ): void {
        handleScheduleFilterUpdate(filter, selectedValue, currentFilter);
        const selectedValues = selectedValue?.map(
            (fltr: any) => fltr.unique_key
        );

        _updateLocalFilterState(filter.filter_key, selectedValues);
    }

    function _updateLocalFilterState(
        selectedFilterKey: any,
        selectedValues?: string[],
        filterComponentType?: string
    ): void {
        const allFilters = Object.assign({}, _selectedFilters);

        const selectedFilterObj = allFilters?.filters
            .filter((fltr: any) => !fltr.locked)
            .filter((fltr: any) => fltr?.filter_key === selectedFilterKey)[0];

        selectedFilterObj?.filter_selection_data?.forEach(
            (filterSelection: any) => {
                filterSelection.selected = false;
            }
        );

        if (selectedValues?.length) {
            selectedValues.forEach((val) => {
                selectedFilterObj?.filter_selection_data?.forEach(
                    (filterSelection: any) => {
                        if (filterComponentType === "text-field" || filterComponentType === "location-search") {
                            filterSelection.value = val;
                        } else {
                            if (filterSelection.unique_key === val) {
                                filterSelection.selected = true;
                            }
                        }
                    }
                );
            });
        } else {
            selectedFilterObj?.filter_selection_data?.forEach(
                (filterSelection: any) => {
                    filterSelection.selected = false;
                }
            );
        }

        _updatedSelectedFilters = _.cloneDeep(allFilters);

        if (handleSelectedFilterUpdate) {
            handleSelectedFilterUpdate(_updatedSelectedFilters);
        }
    }

    function _applyFilter() {
        if (applyFilter) {
            applyFilter(_updatedSelectedFilters);
        }
    }

    return (
        <>
            {_selectedFilters && (
                <div className="d-flex flex-column justify-content-center">
                    <div className="mb-4 d-flex flex-column">
                        <CAFilterContainer
                            selectedFilters={_selectedFilters}
                            searchUpdatedFilter={_searchUpdatedFilter}
                            availabilityFilter={availabilityFilter}
                            setSearchUpdatedFilter={(params: any) => {
                                _handleSelectedFilters(params);
                            }}
                            handleAvailabilityFilter={(params: any) => {
                                _handleAvailabilityFilter(params);
                            }}
                            handleScheduleFilterUpdate={(
                                filter: any,
                                selectedValue: any[],
                                currentFilter: any
                            ) => {
                                _handleScheduleFilterUpdate(
                                    filter,
                                    selectedValue,
                                    currentFilter
                                );
                            }}
                            calenderServiceFilters={calenderServiceFilters}
                            insuranceList={insuranceList || []}
                        />
                    </div>
                </div>
            )}

            <div
                className="pb-4 mb-4 d-flex cursor-pointer"
                onClick={() => {
                    window.location.reload();
                }}
            >
                <Typography variant="size-14" color="color-28" weight="bold">
                    <FontAwesomeIcon
                        className="mr-2"
                        icon={faArrowLeftRotate}
                    />
                    Reset All to Default
                </Typography>
            </div>

            <div>
                <Button
                    label="Save"
                    variant="primary"
                    textWeight="bold"
                    textSize="size-14"
                    className="w-100"
                    onClick={_applyFilter}
                />
            </div>
        </>
    );
}
